import type { CSSProperties } from "react";
import { cssClass } from "./constant";

export type SpeakingStylesType = {
  outerDiv: CSSProperties;
  navRow: CSSProperties;
  navDot: CSSProperties;
  navDotActive: CSSProperties;
  navDotAnswered: CSSProperties;
  navDotFontSize: number;
  stepperFontSize: number;
  stepperButtonSize: number;
  sentenceContainer: CSSProperties;
  playButton: CSSProperties;
  sentenceFontSize: number;
  optionsContainer: CSSProperties;
  optionButton: CSSProperties;
  optionButtonSelected: CSSProperties;
  optionButtonCorrect: CSSProperties;
  optionButtonWrong: CSSProperties;
  optionFontSize: number;
};

export interface SpeakingNavDotDimensions {
  dotSize: number;
  gap: number;
  fontSize: number;
  /**
   * True when the sentence-navigation row is guaranteed, by construction, to fit on a single
   * line at `MIN_DOT_SIZE` or larger. False only when `MIN_DOT_SIZE` has overridden the
   * width-fit calculation (many sentences in a narrow frame) — i.e. every sentence would no
   * longer fit as a same-sized dot on one row. This is the width-derived "too many sentences"
   * threshold both platforms key off of: below it, every sentence gets its own numbered dot;
   * at or above it, both the web editor and the mobile student view replace the dots with a
   * fixed-width `‹ n / m ›` stepper instead (see `calculateSpeakingNavCapacity`) — its width
   * never grows with the sentence count, so it can never overflow the row the way the dots
   * (or an attempted horizontal scroll of them) could. Neither platform wraps onto multiple
   * rows — see the retired `flexWrap: "wrap"` fallback below.
   */
  fitsOnOneRow: boolean;
}

// Matches cssClass.outerDiv's 3% padding on each side (6% of each dimension lost to padding).
const OUTER_PADDING_RATIO = 0.06;
// Gap between nav dots, proportional to dot size so spacing scales with the dots themselves.
const NAV_GAP_RATIO = 0.4;
// Nav dot number size, relative to its own dot.
const NAV_FONT_RATIO = 0.45;
// Floor so many sentences in a narrow frame never collapse the dots to unreadable/zero size.
const MIN_DOT_SIZE = 18;
// Ceiling so a handful of sentences in a wide frame don't grow the dots unreasonably large —
// the nav row is a small index strip, not the element's primary content.
const MAX_DOT_SIZE = 32;
// Slack subtracted from the width budget before solving for dot size, so the row's true occupied
// width never solves to exactly 100% of the available width. See spelling.styles.ts's identical
// constant for why: two independent layout engines (CSS flexbox vs Yoga) can round a fraction of
// a pixel differently, and with zero slack that's enough to flip whether the row wraps on one
// platform but not the other.
const WIDTH_SAFETY_MARGIN_RATIO = 0.05;

/**
 * The nav row's real available width: the element's own width, minus the outer padding every
 * caller already accounts for, minus `reservedWidth` — pixels a caller has already committed to
 * a sibling in the same row (e.g. the web editor's add/remove-sentence buttons, which share the
 * row with the nav dots). Defaults to 0, so a caller with nothing else in the row (mobile, and
 * the web editor when the row is condensed to the stepper) is unaffected.
 *
 * This is the actual fix for a real layout bug: the nav row was previously sized off the whole
 * element's width even when a caller reserved part of that width for something else, so the
 * capacity/dot-size math would say "fits" for a row that, in the real rendered layout, didn't —
 * dots rendered on top of the sibling controls. The fix is to feed the *true* available width in
 * here, not to paper over the mismatch with `overflow` on the container.
 */
function calculateAvailableNavWidth(elementWidth: number, reservedWidth: number): number {
  const paddedWidth = elementWidth * (1 - OUTER_PADDING_RATIO);
  const usableWidth = Math.max(paddedWidth - Math.max(reservedWidth, 0), 0);
  return usableWidth * (1 - WIDTH_SAFETY_MARGIN_RATIO);
}

/**
 * Pure sizing math for the sentence-navigation row (the numbered dots letting a student jump back
 * to an already-answered sentence to view it). Given the row's own available width (see
 * `calculateAvailableNavWidth`) and how many sentences it holds, returns the largest dot size (up
 * to `MAX_DOT_SIZE`) that still fits every dot on one row, accounting for inter-dot gaps and
 * safety slack. Extracted so it's testable without rendering, mirroring `calculateSpellingBoxSize`.
 *
 * This one was already width-derived before the height/width sizing-basis fix below (see
 * `getSpeakingStyles`'s doc comment) — a nav dot's whole job is fitting N-in-a-row along the
 * width axis, so it was never subject to the web/mobile aspect-ratio divergence bug; left as-is.
 */
export function calculateSpeakingNavDotSize(
  elementWidth: number,
  sentenceCount: number,
  reservedWidth = 0
): SpeakingNavDotDimensions {
  const safeSentenceCount = Math.max(sentenceCount, 1);
  const availableWidth = calculateAvailableNavWidth(elementWidth, reservedWidth);

  // n dots + (n-1) gaps, where each gap is a fraction of the (unknown) dot size, must fit
  // availableWidth: n*dot + (n-1)*NAV_GAP_RATIO*dot <= availableWidth
  const widthFitDotSize =
    availableWidth / (safeSentenceCount + (safeSentenceCount - 1) * NAV_GAP_RATIO);

  const unclampedDotSize = Math.min(widthFitDotSize, MAX_DOT_SIZE);
  const dotSize = Math.max(unclampedDotSize, MIN_DOT_SIZE);

  return {
    dotSize,
    gap: dotSize * NAV_GAP_RATIO,
    fontSize: dotSize * NAV_FONT_RATIO,
    // MIN_DOT_SIZE only ever raises dotSize above the width fit, never lowers it, so strict
    // equality against the pre-floor value reliably detects whether the floor won.
    fitsOnOneRow: dotSize === unclampedDotSize,
  };
}

/**
 * The inverse of `calculateSpeakingNavDotSize`'s width-fit inequality: instead of solving for the
 * dot size given a sentence count, solves for the largest sentence count that still fits one row
 * of `MIN_DOT_SIZE` dots (the smallest usable size) in the given available width. This is the
 * width-derived "how many dots fit before switching to the stepper" capacity both platforms key
 * off of — never a hard-coded sentence count, since the element is resizable (and, on the web
 * editor, part of that width is reserved for sibling controls — see `reservedWidth`) so "too
 * many" genuinely depends on how much room is actually left for the row.
 *
 * By construction this agrees exactly with `calculateSpeakingNavDotSize`'s `fitsOnOneRow` for the
 * same `(elementWidth, reservedWidth)` pair: `count <= calculateSpeakingNavCapacity(width, reserved)`
 * iff `calculateSpeakingNavDotSize(width, count, reserved).fitsOnOneRow` (see the "capacity/
 * fitsOnOneRow agreement" test in speaking.styles.test.ts) — both are the same inequality, just
 * solved for a different unknown, so web and mobile can never disagree about where the threshold
 * is even though only the web editor ever passes a non-zero `reservedWidth`.
 */
export function calculateSpeakingNavCapacity(elementWidth: number, reservedWidth = 0): number {
  const availableWidth = calculateAvailableNavWidth(elementWidth, reservedWidth);
  // n*MIN_DOT_SIZE + (n-1)*NAV_GAP_RATIO*MIN_DOT_SIZE <= availableWidth, solved for n.
  const capacity = Math.floor(
    (availableWidth + MIN_DOT_SIZE * NAV_GAP_RATIO) / (MIN_DOT_SIZE * (1 + NAV_GAP_RATIO))
  );
  return Math.max(capacity, 1);
}

// Teacher-adjustable multipliers on the sentence text size and option text size
// (SpeakingElementSchema.sentenceFontScale / optionFontScale in packages/types), applied
// independently. Deliberately ratios applied on top of the frame-derived size, never absolute
// pixel values — every size in this element is frame-relative so web and mobile render
// identically at any resolution; an absolute px field would reintroduce exactly the web/mobile
// divergence the spelling element shipped with and had to fix (see WIDTH_SAFETY_MARGIN_RATIO
// above). Clamped so the text can neither collapse to unreadable/zero nor grow large enough to
// push content out of the frame — the same floor/ceiling pattern MIN_DOT_SIZE/MAX_DOT_SIZE use,
// just for a ratio instead of a pixel size.
export const MIN_FONT_SCALE = 0.5;
export const MAX_FONT_SCALE = 2;
export const DEFAULT_FONT_SCALE = 1;

/**
 * Clamps a teacher-authored font scale into the safe range, defaulting an absent or invalid value
 * to `DEFAULT_FONT_SCALE` (1, i.e. today's unscaled sizes). Used independently for the sentence
 * and option scales — this is the one place that logic lives, so both call sites (and both
 * platforms, via `getSpeakingStyles`) stay in sync. Exported so the clamp behavior itself is
 * independently testable.
 */
export function clampFontScale(fontScale: number | undefined): number {
  if (fontScale === undefined || Number.isNaN(fontScale)) return DEFAULT_FONT_SCALE;
  return Math.min(Math.max(fontScale, MIN_FONT_SCALE), MAX_FONT_SCALE);
}

// ── Width- vs height-derived sizing ───────────────────────────────────────────────────────────
// Root cause (packages/canvas-core/src/utils/coordinateConverter.ts): an element's persisted
// size is a *percentage of its canvas*, converted with `width = (pct/100)*canvasWidth` and
// `height = (pct/100)*canvasHeight` — two different, independent bases. The web editor canvas and
// the mobile student canvas have different aspect ratios (a phone canvas is tall), so the same
// element ends up with a different height-to-width proportion on each platform. Any size derived
// from `elementHeight` inherits that divergence directly — which is exactly why the web preview
// and the mobile render showed different text sizes for "the same" element, and why dragging the
// frame taller moved the text size far more than expected.
//
// The fix is not to touch coordinateConverter.ts (that's a much larger, riskier change affecting
// every element type, not requested here) but to prefer the axis the two platforms diverge least
// on: width. It's also the dimension that actually governs how much text fits on a line, which
// height never did for a left-to-right paragraph of text.
//
// Applied to every *font size* in this file (sentence, option, and the stepper's "n / m" label —
// all genuinely text, all subject to the identical bug) and to the play button (sized to match
// the sentence text it sits directly beside in the same row, so the two don't drift out of
// proportion with each other across platforms). NOT applied to:
//   - the nav dot math above — already width-derived from the start (a dot's job is fitting
//     N-in-a-row along the width axis), never subject to this bug in the first place.
//   - outerDiv/sentenceContainer/optionsContainer/optionButton's gaps and padding — these are
//     vertical spacing between vertically-*stacked* rows (the element's layout is a column), so
//     height is arguably the more natural basis for them, not less; and a little more or less
//     breathing room between rows is a materially smaller, less noticeable divergence than the
//     text itself changing size, which is what was actually reported.
const SENTENCE_FONT_WIDTH_RATIO = 0.07;
const OPTION_FONT_WIDTH_RATIO = 0.0525;
const STEPPER_FONT_WIDTH_RATIO = 0.06;
const STEPPER_BUTTON_WIDTH_RATIO = 0.09;
const PLAY_BUTTON_WIDTH_RATIO = 0.16;

/**
 * Styling for the speaking question: sentence-navigation dots (or, once condensed, the
 * `‹ n / m ›` stepper), the current sentence's play button + text, and its four options.
 * `elementWidth` drives the nav row and every font size (see the "Width- vs height-derived
 * sizing" note above); `elementHeight` drives only vertical spacing between the element's
 * stacked rows. Web and mobile both call this one function so they stay visually consistent —
 * `sentenceFontScale`, `optionFontScale`, and the stepper sizing included: whichever platform
 * authors/renders them, both stay in sync, since neither has its own copy of this math.
 *
 * `reservedNavWidth` (default 0) is the same "width already committed to a sibling in the nav
 * row" concept `calculateSpeakingNavDotSize` takes — pass it through unchanged from whatever the
 * caller used there, so the dot size returned here always matches the fit decision that was made
 * with it.
 */
export function getSpeakingStyles(
  elementWidth: number,
  elementHeight: number,
  sentenceCount: number,
  sentenceFontScale?: number,
  optionFontScale?: number,
  reservedNavWidth = 0
): SpeakingStylesType {
  const {
    dotSize,
    gap,
    fontSize: navDotFontSize,
  } = calculateSpeakingNavDotSize(elementWidth, sentenceCount, reservedNavWidth);
  const sentenceScale = clampFontScale(sentenceFontScale);
  const optionScale = clampFontScale(optionFontScale);
  const playButtonSize = elementWidth * PLAY_BUTTON_WIDTH_RATIO;

  return {
    outerDiv: {
      ...cssClass.outerDiv,
      ...cssClass.fontFamily,
      gap: elementHeight * 0.03,
    },

    // Always a single row, never wraps onto multiple rows (that was the layout-breaking bug this
    // replaced). Below the capacity threshold this holds every sentence's dot, centered, exactly
    // as always. At or above it, the caller renders the fixed-width stepper here instead — this
    // shared style object doesn't need to know which one it's holding, since neither needs
    // special-cased row behavior (no scrolling, no wrapping, always centered).
    navRow: {
      display: "flex",
      flexDirection: "row",
      gap,
      flexWrap: "nowrap",
      justifyContent: "center",
    },

    // Same border-box rationale as spelling.styles.ts's letterBox: RN's Yoga layout is always
    // border-box, so this is pinned explicitly rather than relying on an external CSS reset.
    navDot: {
      width: dotSize,
      height: dotSize,
      boxSizing: "border-box",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      borderRadius: "9999px",
      borderWidth: 2,
      borderStyle: "solid",
      borderColor: "#cbd5e1",
      backgroundColor: "#FFFFFF",
      color: "#64748b",
    },

    // Currently viewed sentence.
    navDotActive: {
      borderColor: "rgb(99, 102, 241)",
      backgroundColor: "rgb(99, 102, 241)",
      color: "#FFFFFF",
    },

    // Already-answered sentence, available to revisit read-only.
    navDotAnswered: {
      borderColor: "#94a3b8",
      backgroundColor: "#F1F5F9",
      color: "#334155",
    },

    navDotFontSize,

    // The "‹ n / m ›" stepper shown once the row is condensed. Its width is constant regardless
    // of sentence count (that's the whole point — it can never overflow); its font/button size is
    // width-derived, same basis as the sentence/option text — see the note above.
    stepperFontSize: elementWidth * STEPPER_FONT_WIDTH_RATIO,
    stepperButtonSize: elementWidth * STEPPER_BUTTON_WIDTH_RATIO,

    sentenceContainer: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: elementHeight * 0.03,
      padding: elementHeight * 0.02,
      borderRadius: "12px",
      backgroundColor: "#FFFFFF",
    },

    playButton: {
      width: playButtonSize,
      height: playButtonSize,
      boxSizing: "border-box",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      borderRadius: "9999px",
      backgroundColor: "#F1F5F9",
      flexShrink: 0,
    },

    sentenceFontSize: elementWidth * SENTENCE_FONT_WIDTH_RATIO * sentenceScale,

    optionsContainer: {
      display: "flex",
      flexDirection: "column",
      gap: elementHeight * 0.02,
    },

    optionButton: {
      boxSizing: "border-box",
      padding: elementHeight * 0.02,
      borderRadius: "12px",
      borderWidth: 2,
      borderStyle: "solid",
      borderColor: "#e5e7eb",
      backgroundColor: "#FFFFFF",
      textAlign: "center",
    },

    // Chosen option, not yet graded (viewing an already-answered sentence before submission).
    optionButtonSelected: {
      borderColor: "rgb(99, 102, 241)",
    },

    // Never used to substitute the correct text over the student's own choice — only to color
    // the box the student actually picked (or, in the review, the correct one alongside it).
    optionButtonCorrect: {
      borderColor: "rgb(35, 205, 0)",
      backgroundColor: "rgb(35, 205, 0)",
    },

    optionButtonWrong: {
      borderColor: "rgb(235, 45, 45)",
      backgroundColor: "rgb(235, 45, 45)",
    },

    optionFontSize: elementWidth * OPTION_FONT_WIDTH_RATIO * optionScale,
  };
}
